import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateEMI, formatCurrency } from "../../utils/calculations";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import ProductLayout from "@/common/components/ProductLayout";

export const EMICalculator = () => {
  const navigate = useNavigate();
  const [loanAmount, setLoanAmount] = useState(2500000);
  const [loanRate, setLoanRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);

  const emiResult = useMemo(
    () => calculateEMI(loanAmount, loanRate, loanTenure),
    [loanAmount, loanRate, loanTenure]
  );

  const emiPieData = [
    { name: "Principal", value: loanAmount, color: "#14b8a6" },
    { name: "Interest", value: emiResult.totalInterest, color: "#ef4444" },
  ];

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                EMI Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-rose-100 p-3">
                      <CreditCard className="h-6 w-6 text-rose-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        EMI Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Calculate your loan repayments
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Loan Amount"
                      value={loanAmount}
                      onChange={setLoanAmount}
                      min={100000}
                      max={100000000}
                      step={100000}
                      prefix="₹"
                      limitLabels={["₹1 L", "₹10 Cr"]}
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Interest rate (p.a)"
                      value={loanRate}
                      onChange={setLoanRate}
                      min={1}
                      max={30}
                      step={0.1}
                      suffix="%"
                      colorClass="text-green-600"
                    />

                    <CompactCalculatorRow
                      label="Tenure (Years)"
                      value={loanTenure}
                      onChange={setLoanTenure}
                      min={1}
                      max={40}
                      step={1}
                      suffix="Yrs"
                      colorClass="text-purple-600"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-border bg-muted/20">
                  <CardContent className="p-8">
                    <div className="mb-8 text-center">
                      <p className="mb-1 font-medium text-muted-foreground">
                        Monthly EMI
                      </p>
                      <h2 className="text-4xl font-black text-primary">
                        {formatCurrency(emiResult.emi)}
                      </h2>
                    </div>

                    <div className="mb-6 grid grid-cols-2 gap-4">
                      <div className="h-[180px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={emiPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={70}
                              dataKey="value"
                            >
                              {emiPieData.map(e => (
                                <Cell key={e.name} fill={e.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(v: number | undefined) =>
                                v !== undefined ? formatCurrency(v) : ""
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-col justify-center space-y-2">
                        {emiPieData.map(item => (
                          <div
                            key={item.name}
                            className="flex items-center gap-2"
                          >
                            <div
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm text-muted-foreground">
                              {item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between rounded-xl bg-slate-50 p-4">
                        <span className="text-muted-foreground">Principal</span>
                        <span className="font-bold">
                          {formatCurrency(loanAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between rounded-xl bg-slate-50 p-4">
                        <span className="text-muted-foreground">
                          Total Interest
                        </span>
                        <span className="font-bold text-rose-600">
                          {formatCurrency(emiResult.totalInterest)}
                        </span>
                      </div>
                      <div className="flex justify-between rounded-xl bg-primary p-4 text-white">
                        <span className="font-bold">Total Amount</span>
                        <span className="text-lg font-bold">
                          {formatCurrency(emiResult.totalPayment)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
